'use client';

import { useTranslations } from 'next-intl';
import { useLayoutEffect, useRef, useState } from 'react';
import { EMarketAction, MarketAction } from '@/lib/types/market-action';
import { cn } from '@/lib/ui-kit/utils';

type BuySellTabsProps = {
  activeTab: MarketAction;
  onActiveTabChange: (tab: MarketAction) => void;
};

export const BuySellTabs = ({ activeTab, onActiveTabChange }: BuySellTabsProps) => {
  const t = useTranslations('common');
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  const tabs = [
    { id: EMarketAction.BUY, label: t('buy') },
    { id: EMarketAction.SELL, label: t('sell') },
  ];

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLButtonElement>(`[data-tab="${activeTab}"]`);
    if (!el) return;
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
  }, [activeTab]);

  return (
    <div ref={listRef} className="relative flex items-center gap-5">
      {indicator && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 h-px bg-v2-text-primary transition-[transform,width] duration-200"
          style={{
            transform: `translateX(${indicator.left}px)`,
            width: indicator.width,
          }}
        />
      )}
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            data-tab={tab.id}
            onClick={() => onActiveTabChange(tab.id as MarketAction)}
            className={cn(
              'cursor-pointer pt-1 pb-1.5 text-[13px] font-medium leading-4 tracking-[-0.13px] transition-colors',
              isActive
                ? 'text-v2-text-primary'
                : 'text-v2-text-secondary hover:text-v2-text-primary',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};
